import { localCompare } from './localCompare';
/**
 * Минимальная длина строки для нечёткого поиска по умолчанию.
 * @constant
 * @type {number}
 * @default
 */
const DEFAULT_MIN_FUZZY_SEARCH_LEN = 3;
/**
 * Сравнение строк без учёта регистра и диакритических знаков
 */
function isLooseEqual(strA, strB) {
    return localCompare(strA ?? '', strB ?? '') === 0;
}
/**
 * Проверяет, совпадают ли подстроки двух строк, начиная с заданных позиций,
 * с учетом нечувствительности к регистру и диакритическим знакам.
 * @param {string} sourceString - Первая строка для сравнения
 * @param {string} targetString - Вторая строка для сравнения
 * @param {number} sourceStart - Начальная позиция в первой строке
 * @param {number} targetStart - Начальная позиция во второй строке
 * @returns {boolean} Возвращает true, если подстроки совпадают от заданных позиций до конца одной из строк
 */
function checkStringsEqualityFrom(sourceString, targetString, sourceStart, targetStart) {
    while (sourceStart < sourceString.length && targetStart < targetString.length) {
        if (!isLooseEqual(sourceString[sourceStart], targetString[targetStart])) {
            return false;
        }
        sourceStart++;
        targetStart++;
    }
    return true;
}
/**
 * Проверяет, отличается ли подстрока целевой строки от искомого текста ровно на одно редактирование
 * (вставка, удаление или замена одного символа).
 *  При сравнении букв игнорируются регистр и диакритические знаки
 *
 * @function isOneEditDistance
 * @param {Object} params - Параметры функции
 * @param {string} params.targetString - Целевая строка для сравнения
 * @param {number} params.targetStringStart - Начальный индекс подстроки в целевой строке
 * @param {number} params.targetStringEnd - Конечный индекс подстроки в целевой строке
 * @param {string} params.searchText - Текст для сравнения с подстрокой
 * @returns {boolean} Возвращает true, если подстрока и искомый текст отличаются ровно на одно редактирование, иначе false
 *
 */
export function isOneEditDistance({ targetString, targetStringStart, targetStringEnd, searchText, }) {
    const targetSubstringLength = targetStringEnd - targetStringStart;
    if (Math.abs(targetSubstringLength - searchText.length) > 1)
        return false;
    for (let i = 0; i < Math.min(targetSubstringLength, searchText.length); i++) {
        if (!isLooseEqual(targetString[i + targetStringStart], searchText[i])) {
            if (targetSubstringLength === searchText.length) {
                return checkStringsEqualityFrom(targetString, searchText, i + 1 + targetStringStart, i + 1);
            }
            if (targetSubstringLength < searchText.length) {
                return checkStringsEqualityFrom(targetString, searchText, i + targetStringStart, i + 1);
            }
            if (targetSubstringLength > searchText.length) {
                return checkStringsEqualityFrom(targetString, searchText, i + 1 + targetStringStart, i);
            }
        }
    }
    // Все предыдущие символы совпадают, проверяем наличие лишнего символа в конце
    return Math.abs(targetSubstringLength - searchText.length) <= 1;
}
/**
 * Возвращает индекс первого вхождения подстроки `searchText` в строке `targetText`
 * При сравнении букв игнорируются регистр и диакритические знаки
 *
 * @param {string} targetText - Строка, в которой осуществляется поиск.
 * @param {string} searchText - Подстрока, которую необходимо найти.
 * @returns {number} Индекс первого вхождения подстроки, или -1, если совпадение не найдено.
 *
 * @example
 * // Пример использования:
 * findSubstringMatchIndex('Привет, Мир!', 'мир') // вернёт 8, если isLooseEqual игнорирует регистр
 */
export function findSubstringMatchIndex(targetText, searchText) {
    if (searchText.length === 0) {
        return 0;
    }
    if (searchText.length > targetText.length) {
        return -1;
    }
    for (let i = 0; i <= targetText.length - searchText.length; i++) {
        let match = true;
        for (let j = 0; j < searchText.length; j++) {
            const firstStr = targetText[i + j] || '';
            const secondStr = searchText[j] || '';
            if (!isLooseEqual(firstStr, secondStr)) {
                match = false;
                break;
            }
        }
        if (match) {
            return i;
        }
    }
    return -1;
}
/**
 * Проверяет, является ли одна строка подстрокой другой без учета регистра и диактрических знаков.
 *
 * @function isSubstringMatch
 * @param {string} targetText - Строка, в которой осуществляется поиск
 * @param {string} searchText - Подстрока, которую нужно найти
 * @returns {boolean} Возвращает true, если searchText является подстрокой targetText
 *
 * @example
 * isSubstringMatch('Привет мир', 'мир');       // true
 * isSubstringMatch('Привет мир', 'пока');      // false
 * isSubstringMatch('привет мир', 'привёт');    // true
 */
export function isSubstringMatch(targetText, searchText) {
    const result = findSubstringMatchIndex(targetText, searchText);
    return result >= 0;
}
/**
 * Находит позицию подстроки, отличающейся от заданного текста на одну правку.
 *
 * @function getOneEditDistanceMatchPosition
 * @param {string} targetString - Строка для поиска
 * @param {string} searchText - Текст для сравнения
 * @param {number} [minFuzzySearchLength=DEFAULT_MIN_FUZZY_SEARCH_LEN] - Минимальная длина для нечеткого поиска
 * @returns {{from: number, to: number} | null} Объект с позициями начала и конца совпадения или null
 */
export function getOneEditDistanceMatchPosition(targetString, searchText, minFuzzySearchLength = DEFAULT_MIN_FUZZY_SEARCH_LEN) {
    if (searchText.length < minFuzzySearchLength)
        return null;
    const lengths = [searchText.length, searchText.length - 1, searchText.length + 1].filter(len => len > 0 && len <= targetString.length);
    for (const len of lengths) {
        for (let i = 0; i <= targetString.length - len; i++) {
            const isMatch = isOneEditDistance({
                targetString,
                targetStringStart: i,
                targetStringEnd: i + len,
                searchText,
            });
            if (isMatch) {
                return { from: i, to: i + len };
            }
        }
    }
    return null;
}
/**
 * Проверяет, содержит ли строка подстроку, отличающуюся от заданной ровно на одну правку.
 *
 *
 * @function containsOneEditDistanceSubstring
 * @param {string} targetString - Строка для поиска
 * @param {string} searchText - Эталонная строка для сравнения
 * @param {number} [minFuzzySearchLength=DEFAULT_MIN_FUZZY_SEARCH_LEN] - Минимальная длина для нечеткого поиска
 * @returns {boolean} True если найдена подстрока с одной правкой
 *
 * @example
 * containsOneEditDistanceSubstring('Программирование', 'програмирование'); // true
 */
export function containsOneEditDistanceSubstring(targetString, searchText, minFuzzySearchLength = DEFAULT_MIN_FUZZY_SEARCH_LEN) {
    const result = getOneEditDistanceMatchPosition(targetString, searchText, minFuzzySearchLength);
    return Boolean(result);
}
/**
 * Возвращает границы точного совпадения или совпадения с одной опечаткой.
 * Точное совпадение имеет приоритет. Конец диапазона не включается в совпадение.
 *
 * @param targetString Строка для поиска
 * @param searchText Искомый текст
 * @param options Настройки поиска
 * @param options.minFuzzySearchLength Минимальная длина запроса для поиска с опечаткой, по умолчанию 3
 * @returns Границы совпадения в исходной строке или null, если совпадения нет
 *
 * @example
 * getFuzzyMatchPosition('Статистика', 'статистикка'); // { from: 0, to: 10 }
 */
export function getFuzzyMatchPosition(targetString, searchText, { minFuzzySearchLength = DEFAULT_MIN_FUZZY_SEARCH_LEN } = {}) {
    const exactIndex = findSubstringMatchIndex(targetString, searchText);
    if (exactIndex !== -1) {
        return { from: exactIndex, to: exactIndex + searchText.length };
    }
    if (searchText.length >= minFuzzySearchLength) {
        return getOneEditDistanceMatchPosition(targetString, searchText, minFuzzySearchLength);
    }
    return null;
}
/**
 * Проверяет наличие точного или приблизительного совпадения подстроки с учетом опечаток.
 * Сначала проверяет точное вхождение подстроки, затем (если длина searchText позволяет) - совпадения с одной ошибкой.
 *
 * @function findIsFuzzyMatch
 * @param {string} targetString - Строка, в которой производится поиск
 * @param {string} searchText - Искомая подстрока
 * @param {Object} [options] - Настройки поиска
 * @param {number} [options.minFuzzySearchLength=3] - Минимальная длина строки чтобы активировать поиск с учетом опечаток
 * @returns {boolean} True, если найдено точное совпадение или совпадение с одной ошибкой (при достаточной длине)
 *
 * @example
 * // Простое точное совпадение
 * findIsFuzzyMatch('Программирование', 'грамм') //true;
 *
 * @example
 * // Совпадение с одной ошибкой (достаточная длина)
 * findIsFuzzyMatch('програММирование', 'програМирование') //true;
 *
 * @example
 * // Слишком короткая строка для поиска с опечатками
 * findIsFuzzyMatch('код', 'кд', { minFuzzySearchLength: 3 }) //false;
 */
export function findIsFuzzyMatch(targetString, searchText, { minFuzzySearchLength = DEFAULT_MIN_FUZZY_SEARCH_LEN } = {}) {
    return getFuzzyMatchPosition(targetString, searchText, { minFuzzySearchLength }) !== null;
}
/**
 * Проверяет, совпадает ли хотя бы один из синонимов с искомым текстом.
 * Для совпадения учитывается минимальная длина подстроки (не меньше `DEFAULT_MIN_FUZZY_SEARCH_LEN` или длины синонима).
 *
 * @param {string[]} synonyms - Массив поисковых синонимов для поиска
 * @param {string} searchText - Искомый текст
 * @param {number} [options.minSynonymSearchLength=3] - Минимальная длина строки чтобы активировать поиск по синонимам
 * @returns {boolean} Возвращает `true`, если хотя бы один синоним совпадает с частью искомого текста
 *
 * @example
 * // Возвращает true
 * findIsMatchBySynonymsSearch(['кот', 'кошка'], 'котенок');
 *
 * @example
 * // Возвращает false (слишком короткий искомый текст)
 * findIsMatchBySynonymsSearch(['кошка'], 'кот');
 */
export function findIsMatchBySynonymsSearch(synonyms, searchText, { minSynonymSearchLength = DEFAULT_MIN_FUZZY_SEARCH_LEN } = {}) {
    if (synonyms.length === 0)
        return false;
    return synonyms.some(synonym => searchText.length >= Math.min(synonym.length, minSynonymSearchLength) &&
        isSubstringMatch(synonym, searchText));
}
